import { useId, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { analyzeLocalImage, describeLocalVisionError, type LocalVisionSummary } from '../ai/localVision';

export type ImageReadingKind = 'palm' | 'face';

export interface ImageReadingDraft {
  kind: ImageReadingKind;
  dataUrl: string;
  mimeType: string;
  fileName: string;
}

interface ImageReadingInputProps {
  value: ImageReadingDraft | null;
  onChange: (value: ImageReadingDraft | null) => void;
  onAnalysis: (value: LocalVisionSummary | null) => void;
  disabled?: boolean;
  browserModelSelected?: boolean;
}

const MAX_IMAGE_BYTES = 6 * 1024 * 1024;
const MAX_IMAGE_EDGE = 1600;
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

interface OptimizedImage {
  dataUrl: string;
  mimeType: string;
}

function optimizeImageDataUrl(dataUrl: string, mimeType: string): Promise<OptimizedImage> {
  if (typeof document === 'undefined' || typeof Image === 'undefined') {
    return Promise.resolve({ dataUrl, mimeType });
  }
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) {
    return Promise.resolve({ dataUrl, mimeType });
  }
  const canvas = document.createElement('canvas');
  let context: CanvasRenderingContext2D | null = null;
  try {
    context = canvas.getContext('2d');
  } catch {
    return Promise.resolve({ dataUrl, mimeType });
  }
  if (!context) return Promise.resolve({ dataUrl, mimeType });

  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const fallback = () => {
      if (settled) return;
      settled = true;
      resolve({ dataUrl, mimeType });
    };
    image.onload = () => {
      if (settled) return;
      try {
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        if (!width || !height) return fallback();
        const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(width, height));
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        context?.drawImage(image, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL('image/webp', 0.86);
        if (!compressed || compressed === 'data:,') return fallback();
        settled = true;
        resolve({ dataUrl: compressed, mimeType: 'image/webp' });
      } catch {
        fallback();
      }
    };
    image.onerror = fallback;
    image.src = dataUrl;
  });
}

export function ImageReadingInput({ value, onChange, onAnalysis, disabled = false, browserModelSelected = false }: ImageReadingInputProps) {
  const inputId = useId();
  const typeId = useId();
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState<LocalVisionSummary | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const readRequestRef = useRef(0);

  function handleKindChange(kind: ImageReadingKind) {
    readRequestRef.current += 1;
    setError('');
    setAnalysis(null);
    onAnalysis(null);
    if (value) onChange({ ...value, kind });
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const requestId = readRequestRef.current + 1;
    readRequestRef.current = requestId;
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
      setAnalysis(null);
      onAnalysis(null);
      setError('只支持 JPG、PNG 或 WebP 图片。');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setAnalysis(null);
      onAnalysis(null);
      setError('图片不能超过 6 MB，请先压缩后再试。');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      if (typeof reader.result !== 'string') {
        setError('图片读取失败，请换一张图片。');
        return;
      }
      const optimized = await optimizeImageDataUrl(reader.result, file.type);
      if (readRequestRef.current !== requestId) return;
      setError('');
      setAnalysis(null);
      onAnalysis(null);
      onChange({
        kind: value?.kind ?? 'palm',
        dataUrl: optimized.dataUrl,
        mimeType: optimized.mimeType,
        fileName: file.name
      });
    };
    reader.onerror = () => setError('图片读取失败，请换一张图片。');
    reader.readAsDataURL(file);
  }

  async function runLocalAnalysis() {
    if (!value || analyzing) return;
    const requestId = readRequestRef.current;
    setAnalyzing(true);
    setError('');
    try {
      const result = await analyzeLocalImage(value.kind, value.dataUrl);
      if (readRequestRef.current !== requestId) return;
      setAnalysis(result);
      onAnalysis(result);
    } catch (cause) {
      if (readRequestRef.current !== requestId) return;
      setAnalysis(null);
      onAnalysis(null);
      setError(describeLocalVisionError(cause).message);
    } finally {
      setAnalyzing(false);
    }
  }

  function removeImage() {
    readRequestRef.current += 1;
    setError('');
    setAnalysis(null);
    onAnalysis(null);
    onChange(null);
  }

  return (
    <section className="image-reading-panel" aria-labelledby={`${inputId}-title`}>
      <div className="image-reading-heading">
        <div>
          <span className="eyebrow">可选图片研究</span>
          <h3 id={`${inputId}-title`}>手相 / 面相图片</h3>
        </div>
        <span className="image-local-badge">默认只在本页内存</span>
      </div>
      <p className="image-reading-intro">上传清晰、正面且已获得授权的图片，用于传统文化文本对照。它不用于身份、健康、性格或命运的确定判断。</p>
      <label className="field" htmlFor={typeId}>
        <span>图像研究类型</span>
        <select id={typeId} value={value?.kind ?? 'palm'} onChange={(event) => handleKindChange(event.target.value as ImageReadingKind)} disabled={disabled}>
          <option value="palm">手相（手掌纹理）</option>
          <option value="face">面相（面部可见结构）</option>
        </select>
      </label>
      <label className="field image-upload-field" htmlFor={inputId}>
        <span>上传手相或面相图片 <em>可选</em></span>
        <input id={inputId} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} disabled={disabled} />
        <small>支持 JPG、PNG、WebP，单张不超过 6 MB；图片会先在本机缩放压缩，不会写入 localStorage、Cookie 或本站服务器。</small>
      </label>
      {error && <p className="form-error" role="alert">{error}</p>}
      {browserModelSelected && <p className="image-model-note" role="status">先点击“本机分析图片”；当前解读方式只读取去身份化的结构摘要，原图不会上传。</p>}
      {value && (
        <div className="image-preview-card">
          <img src={value.dataUrl} alt={`待研究的${value.kind === 'palm' ? '手相' : '面相'}图片预览`} />
          <div>
            <strong>{value.fileName}</strong>
            <span>{value.kind === 'palm' ? '手相图片' : '面相图片'} · 尚未发送</span>
            <button type="button" className="text-button" onClick={removeImage} disabled={disabled}>移除图片</button>
          </div>
        </div>
      )}
      {value && <div className="image-analysis-actions">
        <button type="button" className="secondary-button" onClick={runLocalAnalysis} disabled={disabled || analyzing}>
          {analyzing ? '本机分析中…' : '本机分析图片'}
        </button>
        <small>首次使用会下载 MediaPipe 的 WebAssembly 与模型文件；图片只在当前浏览器内处理。</small>
      </div>}
      {analysis && <div className="image-analysis-result" role="status">
        <strong>本机结构分析完成</strong>
        <span>检测到 {analysis.detectedCount} 个{analysis.kind === 'palm' ? '手部' : '面部'}目标；每个目标约 {analysis.landmarkCount || '未获得'} 个关键点。</span>
        <small>这不是身份识别、医学判断或传统断语；浏览器本地 AI 只会收到这段结构摘要。</small>
      </div>}
      <p className="image-consent-note">请勿上传他人图片，除非已取得明确同意。原图不会发送到远程接口；当前图片研究只在浏览器本地处理。</p>
    </section>
  );
}

export { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_BYTES };
